import Base_URL from '../config/apiConfig';
import axiosInstance from './axiosInstance';
import ORCH_BASE_URL from '../config/orchConfig';
import orchestratorAxios from './orchestratorAxios';

const ORCH_API_URL = `${ORCH_BASE_URL}`;

const CONNECTIONS_URL = `${Base_URL}/connections/`;
const SQL_CONNECTOR_URL = `${Base_URL}/api/sql-connector`;

// Cache for SQL table fields to prevent duplicate API calls
const sqlTableFieldsCache = new Map();

// Generate cache key from token and tableName
const getCacheKey = (token, tableName) => {
  return `${token}::${tableName}`;
};

export const fetchAllConnections = async () => {
  try {
    const response = await axiosInstance.get(CONNECTIONS_URL);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Failed to fetch connections:', error);
    return [];
  }
};

export const getConnectionById = async (connId) => {
  try {
    const response = await axiosInstance.get(`${CONNECTIONS_URL}${connId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch connection ${connId}:`, error);
    throw error;
  }
};

export const updateConnection = async (connId, payload) => {
  try {
    const response = await axiosInstance.put(
      `${CONNECTIONS_URL}update/${connId}`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to update connection ${connId}:`, error);
    throw error;
  }
};

export const fetchTypeBasedConnection = async (label) => {
  try {
    const response = await axiosInstance.get(`${CONNECTIONS_URL}type/${label}`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error(`Failed to fetch connections by type "${label}":`, error);
    return [];
  }
};

export const testConnection = async (payload) => {
  try {
    const response = await axiosInstance.post(`${CONNECTIONS_URL}`, payload);
    return response.data;
  } catch (error) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(
      'Connection to the SaaS has failed. Please check your credentials.',
    );
  }
};

export const deleteConnection = async (connectionId) => {
  try {
    const response = await axiosInstance.delete(
      `${CONNECTIONS_URL}${connectionId}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      `Failed to delete connection with ID ${connectionId}:`,
      error,
    );
    throw error;
  }
};

export const fetchJiraProjects = async (conn_id) => {
  try {
    const response = await orchestratorAxios.get(
      `${ORCH_API_URL}/api/v1/jira/projects`,
      {
        params: { conn_id },
      },
    );
    return response.data;
  } catch (error) {
    console.error('Failed to fetch JIRA projects:', error);
    throw new Error('Unable to fetch projects. Please try again.');
  }
};

export const addProjectKeyToJiraConnection = async (conn_id, project_key) => {
  try {
    const response = await orchestratorAxios.patch(
      `${ORCH_API_URL}/api/v1/jira/connection/${conn_id}/add-project-key`,
      null,
      { params: { project_key } },
    );
    return response.data;
  } catch (error) {
    throw new Error('Unable to send projects. Please try again.');
  }
};

export const sqlTestConnection = async (token) => {
  const res = await axiosInstance.get(
    `${Base_URL}/api/sql-connector/test-connection/${encodeURIComponent(token)}`,
  );
  return res.data;
};

export const fetchSqlTables = async (token) => {
  if (!token) {
    throw new Error('SQL token is required to fetch tables.');
  }
  try {
    const res = await axiosInstance.get(
      `${SQL_CONNECTOR_URL}/list-tables/${encodeURIComponent(token)}`,
    );
    return res.data;
  } catch (error) {
    console.error('Failed to fetch SQL tables:', error);
    throw (
      error.response?.data?.detail || error.response?.data?.message || error
    );
  }
};

export const fetchSqlTableFields = async (token, tableName) => {
  if (!token) {
    throw new Error('SQL token is required to fetch table fields.');
  }
  if (!tableName) {
    throw new Error('Table name is required to fetch table fields.');
  }

  // Check cache first
  const cacheKey = getCacheKey(token, tableName);
  if (sqlTableFieldsCache.has(cacheKey)) {
    console.log(`[Cache Hit] SQL table fields for ${tableName}`);
    return sqlTableFieldsCache.get(cacheKey);
  }

  // If already fetching, wait for the existing promise
  const pendingKey = `pending_${cacheKey}`;
  if (sqlTableFieldsCache.has(pendingKey)) {
    console.log(
      `[Cache Pending] Waiting for existing request for ${tableName}`,
    );
    return await sqlTableFieldsCache.get(pendingKey);
  }

  // Create new request and store promise
  const requestPromise = (async () => {
    try {
      const res = await axiosInstance.get(
        `${SQL_CONNECTOR_URL}/table-fields/${encodeURIComponent(
          token,
        )}/${encodeURIComponent(tableName)}`,
      );
      const data = res.data;
      // Store in cache and remove pending flag
      sqlTableFieldsCache.set(cacheKey, data);
      sqlTableFieldsCache.delete(pendingKey);
      return data;
    } catch (error) {
      // Remove pending flag on error
      sqlTableFieldsCache.delete(pendingKey);
      console.error(`Failed to fetch SQL fields for ${tableName}:`, error);
      throw (
        error.response?.data?.detail || error.response?.data?.message || error
      );
    }
  })();

  // Store pending promise
  sqlTableFieldsCache.set(pendingKey, requestPromise);
  return await requestPromise;
};

export const updateHostPort = async (connId, payload) => {
  const res = await axiosInstance.patch(
    `${Base_URL}/connections/update-host-port/${encodeURIComponent(connId)}`,
    payload,
  );
  return res.data;
};
